<template>
  <div :class="classes()">
    <component :is="!isAuthenticated ? 'Container' : 'div'">
      <div :class="`${el}__inner`">
        <div v-if="testing && $matchMedia.l" :class="`${el}__debugElement`">
          <pre>
            PG: {{ getCalculatedCareLevel(true).pflegegrad || '-' }}
            Punkte: {{ getCalculatedCareLevel(true).gewichtetePunkte }}
          </pre>
        </div>
        <Statusbar
          :class="`${el}__statusBar`"
          :currentStep="currentModule + 1"
          :headline="$t('careLevelCalculator.statusbar.headline')"
          :statusText="$t('careLevelCalculator.statusbar.statusText')"
          :steps="wizard.length"
        />

        <Divider v-if="$matchMedia.l" dotted />

        <div :key="moduleKey" :class="`${el}__row`">
          <InViewAnimation>
            <Headline slot="0" :staticClass="`${el}__moduleHeadline`" responsive size="m">
              {{ currentModule + 1 }}. {{ wizard[currentModule].headline }}
            </Headline>
          </InViewAnimation>

          <div :class="`${el}__content`">
            <template v-if="showModuleOverview">
              <ModuleOverview
                v-for="(item, idx) in $t(
                  `careLevelCalculator.wizard.moduleOverview.${currentModule + 1}`
                )"
                :key="`moduleOverview-${idx}`"
                :contentIdx="idx"
                :currentModule="currentModule"
                :el="el"
                :hasInputCounter="
                  wizard[currentModule].questions.filter((q) => q.withInputCounter).length > 0
                "
                :headline="wizard[currentModule].headline"
              />
            </template>

            <template v-else>
              <Mobile
                v-if="!$matchMedia.l"
                :currentModule="currentModule"
                :el="el"
                :loading="loading"
                :wizard="wizard"
                @change="onChange"
                @nextPage="nextPage"
                @onInputCounterChange="onInputCounterChange"
              />

              <Desktop
                v-else
                :currentModule="currentModule"
                :el="el"
                :valid="desktopModuleIsValid"
                :wizard="wizard"
                @change="onChange"
                @nextPage="nextPage"
                @onInputCounterChange="onInputCounterChange"
              />
            </template>
          </div>
        </div>

        <Divider v-if="$matchMedia.l" dotted />

        <div :class="`${el}__row`">
          <div :class="`${el}__buttonRow`">
            <Link
              v-if="$matchMedia.l || showPrevLink"
              :class="{
                [`${el}__navLink`]: true,
                [`${el}__navLink--hidden`]: !showPrevLink,
              }"
              icon="arrow-left-line"
              noUnderline
              role="link"
              tabindex="0"
              @click="prevPage"
              @keyup.enter.native="prevPage"
              @keyup.space.native="prevPage"
            >
              {{ $t('careLevelCalculator.backButton') }}
            </Link>

            <div :class="`${el}__buttonInnerRow`">
              <Button
                v-if="showModuleOverview"
                :disabled="!enableCurrentOverviewButton"
                :expand="!$matchMedia.l"
                :label="$t('careLevelCalculator.nextButton')"
                :loading="loading"
                additionalIconName="arrow-right-line"
                small
                @click="onClickModuleOverview"
              />

              <Button
                v-if="!showModuleOverview && $matchMedia.l"
                :class="{
                  [`${el}__navLink`]: true,
                }"
                :label="
                  currentModule === wizard.length - 1
                    ? $t('careLevelCalculator.resultButton')
                    : $t('careLevelCalculator.nextModuleButton')
                "
                :loading="loading"
                additionalIconName="arrow-right-line"
                small
                @click="handleModuleSubmit"
              />
            </div>
          </div>
        </div>
      </div>
    </component>
  </div>
</template>

<script>
import Vue from 'vue';
import Container from '@/objects/Container';
import Headline from '@/objects/Headline';
import Link from '@/objects/Link';
import Button from '@/objects/Button';
import Icon from '@/objects/Icon';
import Divider from '@/objects/Divider';
import IntroBanner from '@/components/IntroBanner';
import Row from '@/components/Grid/Row';
import Col from '@/components/Grid/Col';
import InViewAnimation from '@/components/InViewAnimation';
import Statusbar from '@/components/Statusbar';
import {
  CARELEVEL_CLEAR_WIZARD,
  CARELEVEL_RESET_MODULE_ANSWERS,
  CARELEVEL_SET_TRACKING_UUID,
  CARELEVEL_UPDATE_ANSWER,
  CARELEVEL_UPDATE_INPUT_VALUES,
} from '@/store/actions/careLevelCalculator';

import {
  ROUTE_ANALYSE_CARE_LEVEL,
  ROUTE_SERVICE_CARELEVEL_CALCULATOR,
  ROUTE_SERVICE_CARELEVEL_CALCULATOR_PREWIZARD,
  ROUTE_SERVICE_CARELEVEL_CALCULATOR_REGISTER,
  ROUTE_SERVICE_CARELEVEL_CALCULATOR_WIZARD,
} from '@/constants/routes';

import { mapFieldsAuth, mapFieldsCareLevelCalculator, mapFieldsHealth } from '@/store/mapFields';

import wizard from '@/content/services/CareLevelCalculator/modules';

import CLCWizardKonami from '@/views/CareLevelCalculator/Wizard/CLCWizardKonami';
import careLevelMixin from '@/mixins/careLevel';
import {
  ROUTE_API_SAVE_UNREGISTERED_CARE_LEVEL_WIZARD,
  ROUTE_API_UPDATE_CARE_LEVEL_DATA,
} from '@/constants/apiRoutes';
import { HEALTH_REQUEST } from '@/store/actions/health';

import { CONTENT_SHEET_CONTENT } from '@/store/actions/contentSheet';

import {
  LS_CARE_LEVEL_CALCULATOR_JWT,
  LS_CARE_LEVEL_CALCULATOR_NEWSLETTER_TOKEN,
} from '@/constants/storage';

import ModuleOverview from './subcomponents/ModuleOverview';
import Desktop from './subcomponents/Desktop';
import Mobile from './subcomponents/Mobile';

import { isProd } from '@/helpers/tracking/utils/helpers';
import { getBiTrackingData } from '@/helpers/biTracking/biTracking';

/**
 * Um die Modulübersichetseiten wieder einzublenden,
 * hier auf true setzen
 */
export const SHOW_MODULE_OVERVIEW_PAGES = false;

export default {
  name: 'CareLevelCalculatorWizard',
  props: {},
  mixins: [careLevelMixin, !isProd() ? CLCWizardKonami : ''],
  components: {
    Headline,
    Link,
    Button,
    Icon,
    Divider,
    Container,
    IntroBanner,
    Row,
    Col,
    InViewAnimation,
    Statusbar,
    ModuleOverview,
    Mobile,
    Desktop,
  },
  data() {
    return {
      testing: !!process.env.VUE_APP_TESTING,
      loading: false,
      moduleKey: 1,
      wizard,
      currentQuestion: 0,
      showModuleOverview: true,
      desktopModuleIsValid: true,
      ROUTE_SERVICE_CARELEVEL_CALCULATOR,
      ROUTE_SERVICE_CARELEVEL_CALCULATOR_PREWIZARD,
      ROUTE_SERVICE_CARELEVEL_CALCULATOR_WIZARD,
      el: 'v-careLevelCalculatorWizard',
      classes: () => {
        const el = this.el;
        return {
          [el]: true,
        };
      },
    };
  },
  created() {
    if (this.step > 0 && this.isAuthenticated) {
      this.$router.push(ROUTE_SERVICE_CARELEVEL_CALCULATOR_WIZARD);
    }

    if (!this.isAuthenticated && this.careLevelSituation === null) {
      this.$router.push(`${ROUTE_SERVICE_CARELEVEL_CALCULATOR_PREWIZARD}/2`);
    }

    this.setShowModelOverview();
  },
  computed: {
    ...mapFieldsHealth([
      'careLevel',
      'dependentPerson',
      'careLevelSituation',
      'healthUuid',
      'careLevelDate',
    ]),
    ...mapFieldsCareLevelCalculator([
      'activeWizardAnswers',
      'overViewRadioButtons',
      'lastVisitedWizardStep',
      'currentWizardIsComplete',
      'currentWizardUserValue',
      'activeCounterOverviewInputValue',
      'trackingUuid',
    ]),
    ...mapFieldsAuth(['isAuthenticated']),

    step() {
      let { step } = this.$route.params;
      return parseInt(step) || 0;
    },

    currentModule() {
      return Math.floor(this.step / 2) || 0;
    },

    enableCurrentOverviewButton() {
      const questionLength = this.$t(
        `careLevelCalculator.wizard.moduleOverview.${this.currentModule + 1}`
      ).length;

      return (
        this.overViewRadioButtons[this.currentModule] &&
        Object.keys(this.overViewRadioButtons[this.currentModule]).length === questionLength
      );
    },

    showPrevLink() {
      return !(this.isAuthenticated && this.step === 0);
    },
  },
  watch: {
    $route() {
      if (this.step >= this.lastVisitedWizardStep) {
        this.lastVisitedWizardStep = this.step;
      }
      window.scrollTo(0, 0);

      this.moduleKey += 1;
    },

    step() {
      this.setShowModelOverview();
    },

    currentModule() {
      this.currentQuestion = -1;

      setTimeout(() => {
        const items = this.activeWizardAnswers[this.currentModule].filter((aw) => aw !== null);
        this.currentQuestion = items.length > 0 ? items.length - 1 : 0;
      }, 1);
    },
  },
  beforeDestroy() {
    if (this.isAuthenticated) {
      this.$store.dispatch(HEALTH_REQUEST);
    }
  },

  methods: {
    onCloseIntroBanner() {
      this.currentWizardIsComplete = false;
    },

    loadSavedWizardData() {
      this.loadWizardDataFromUserValue();

      if (!this.currentWizardIsComplete) {
        let goToStep =
          this.activeWizardAnswers.filter((answer) => answer.filter((a) => a !== null).length)
            .length + this.overViewRadioButtons.filter((a) => a !== null).length;
        this.$router.push(`${ROUTE_SERVICE_CARELEVEL_CALCULATOR_WIZARD}/${goToStep}`);
      }
    },

    openRegisterOffcanvas() {
      this.$store.dispatch(CONTENT_SHEET_CONTENT, {
        sheetId: 'register/Pgr',
      });
    },

    setShowModelOverview() {
      this.showModuleOverview = SHOW_MODULE_OVERVIEW_PAGES ? this.step % 2 === 0 : false;
    },

    handleModuleSubmit() {
      this.desktopModuleIsValid = true;
      setTimeout(() => {
        this.desktopModuleIsValid =
          this.activeWizardAnswers[this.currentModule].filter((aw) => aw === null).length === 0;

        if (this.desktopModuleIsValid) {
          this.nextPage();
        }
      }, 1);
    },

    prevPage() {
      let prevStep = this.step - (SHOW_MODULE_OVERVIEW_PAGES ? 1 : 2);

      let route = `${ROUTE_SERVICE_CARELEVEL_CALCULATOR_WIZARD}/${prevStep}`;

      if (this.step === 0) {
        route = `${ROUTE_SERVICE_CARELEVEL_CALCULATOR_PREWIZARD}/2`;
      }

      this.$router.push(route);
    },

    nextPage: function () {
      let route = `${ROUTE_SERVICE_CARELEVEL_CALCULATOR_WIZARD}/${
        this.step + (SHOW_MODULE_OVERVIEW_PAGES ? 1 : 2)
      }`;

      if (this.currentModule === this.wizard.length - 1 && !this.showModuleOverview) {
        this.loading = true;
        this.saveWizard()
          .then(() => {
            route = this.isAuthenticated
              ? ROUTE_ANALYSE_CARE_LEVEL
              : ROUTE_SERVICE_CARELEVEL_CALCULATOR_REGISTER;
            this.loading = false;
            if (this.isAuthenticated) {
              this.$store.dispatch(CARELEVEL_CLEAR_WIZARD);
            }

            this.$router.push(route);
          })
          .catch(() => {
            this.$router.push({
              path: ROUTE_SERVICE_CARELEVEL_CALCULATOR,
              query: { statusCode: 404 },
            });
          });
      } else {
        if (!this.isAuthenticated) {
          this.$router.push(route);
        } else {
          // Speichere jedes Modul in der Datenbank
          this.loading = true;
          Vue.axios
            .post(ROUTE_API_UPDATE_CARE_LEVEL_DATA, {
              careLevelWizard: {
                userValue: JSON.stringify(this.getUserValues()),
                isComplete: this.currentModule === this.wizard.length ? 1 : 0,
              },
              tracking: getBiTrackingData(),
            })
            .then(() => {
              this.loading = false;
              this.$router.push(route);
            });
        }
      }
    },

    getUserValues() {
      return {
        activeWizardAnswers: this.activeWizardAnswers,
        activeCounterOverviewInputValue: this.activeCounterOverviewInputValue,
        activeCounterInputValues: this.activeCounterInputValues,
        overViewRadioButtons: this.overViewRadioButtons,
      };
    },

    saveWizard() {
      return new Promise((resolve, reject) => {
        const { modulePoints, backendObject } = this.calculatedModulePoints;
        const { gewichtetePunkte, pflegegrad } = this.getCalculatedCareLevel(true);
        const isBackendObjectValid = backendObject['modul1_1'] && backendObject['modul6_1'];
        if (!isBackendObjectValid) {
          reject();
        }
        const basicData = {
          careLevelJson: JSON.stringify(backendObject),
          calculatedCareLevelAt: new Date(),
          calculatedCareLevel: pflegegrad || 0,
          modulePointsJson: JSON.stringify(modulePoints),
          calculatedPoints: gewichtetePunkte,
          careLevelSituation: this.careLevelSituation,
        };
        if (this.isAuthenticated) {
          const data = {
            personInNeedOfCareUuid: this.healthUuid,
            ...basicData,
            careLevel: this.careLevel,
            userValue: JSON.stringify(this.getUserValues()),
            isComplete: 1,
          };
          Vue.axios
            .post(ROUTE_API_UPDATE_CARE_LEVEL_DATA, {
              careLevelWizard: data,
              tracking: getBiTrackingData(),
            })
            .then(() => {
              this.$store.dispatch(HEALTH_REQUEST).then(resolve).catch(reject);
            })
            .catch(reject);
        } else {
          this.dependentPerson = localStorage.getItem('dependentPerson') || null;
          localStorage.removeItem('dependentPerson');
          const data = {
            ...basicData,
            isProfileOwner: this.dependentPerson === 'me' ? 1 : 0,
            careLevel: this.careLevel,
          };
          if (this.careLevelSituation === 0 || this.careLevelSituation === 1) {
            data.careLevelDate = this.careLevelDate || null;
          }

          data.userValue = JSON.stringify(this.getUserValues());
          data.isComplete = 1;

          Vue.axios
            .post(ROUTE_API_SAVE_UNREGISTERED_CARE_LEVEL_WIZARD, {
              careLevelWizard: data,
              tracking: getBiTrackingData('pgr_result'),
            })
            .then((response) => {
              const { jwt, newsletterToken, trackingUuid } = response.data;
              localStorage.setItem(LS_CARE_LEVEL_CALCULATOR_JWT, jwt);
              localStorage.setItem(LS_CARE_LEVEL_CALCULATOR_NEWSLETTER_TOKEN, newsletterToken);
              this.$store.dispatch(CARELEVEL_SET_TRACKING_UUID, trackingUuid);
              resolve();
            })
            .catch(reject);
        }
      });
    },

    onChange(question, index) {
      if (wizard[this.currentModule].module === 5) {
        this.onInputCounterChange(question, 1);
      }

      this.$store.dispatch(CARELEVEL_UPDATE_ANSWER, {
        currentModule: this.currentModule,
        question,
        index,
      });
    },

    onInputCounterChange(question, value) {
      this.$store.dispatch(CARELEVEL_UPDATE_INPUT_VALUES, {
        question,
        value,
      });
    },

    onClickModuleOverview() {
      if (this.overViewRadioButtons[this.currentModule] === 0) {
        this.$store.dispatch(CARELEVEL_RESET_MODULE_ANSWERS, {
          currentModule: this.currentModule,
        });
      }
      this.nextPage();
    },
  },
};
</script>
